import type {
  FindBatchesSiteSelectQuery,
  FindBatchesSiteSelectQueryVariables,
} from 'types/graphql'
import type { CellSuccessProps, CellFailureProps } from '@cedarjs/web'
import { CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material"
import { t } from "i18next"
import { navigate, routes, useLocation, useMatch, useParams } from "@cedarjs/router"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/free-solid-svg-icons"
import BatchSelect from "../BatchSelect/BatchSelect"
import { useConfigurationStore, useStateStore } from "src/lib/store"
import BatchReportTargetSelect from "../BatchReportTargetSelect/BatchReportTargetSelect"

interface BatchesSelectCellProps {
  idSite: Number,
  select: { path?: string, urlSearchString?: string, rootPath?: string }
}
export const beforeQuery = ({ idSite, state }) => {

  return { variables: { idSite, state: state || null } }
}

const QUERY_SITE = gql`
  query FindBatchesSiteSelectQuery($idSite: Int, $state: BatchState) {
    batches(idSite: $idSite, state: $state) {
      id
      name
      harvest {
        id
      }
      report {
        id
      }
      isStopped
      quantity
      batchInitial {
        stadePL
        growModel {
          name
        }
        provider {
          name
        }
      }
    }
  }
`
const QUERY_ALL = gql`
  query FindBatchesAllSelectQuery($state: BatchState) {
    batches(state: $state) {
      id
      name
      aiRequestsCount
      harvest {
        id
      }
      report {
        id
      }
      isStopped
      quantity
      batchInitial {
        stadePL
        growModel {
          name
        }
        provider {
          name
        }
      }
    }
  }
`
/**
 * https://github.com/redwoodjs/redwood/blob/main/packages/web/src/components/createCell.tsx#L133
 * The GraphQL syntax tree to execute or function to call that returns it.
 * If `QUERY` is a function, it's called with the result of `beforeQuery`.
 */
export const QUERY = ({ variables }) => {
  if (variables.idSite)
    return QUERY_SITE
  else
    return QUERY_ALL
}

export const Loading = () => (
  <div className="flex w-full h-14 items-center justify-around px-4 py-2  dark:bg-black/20 bg-slate-100/60 backdrop-blur-xl shadow-md relative z-20">
    <FormControl className="w-44" size="small">
      <InputLabel id="batch">{t("labels.batch")}</InputLabel>
      <Select label={t("labels.batch")} labelId="batch" className="" defaultValue=""></Select>
    </FormControl>
  </div>
)

export const Empty = () =>
  <div className="flex w-full h-14 items-center justify-around px-4 py-2  dark:bg-black/20 bg-slate-100/60 backdrop-blur-xl shadow-md relative z-20">
    <FormControl className="w-44" size="small">
      <InputLabel id="batch">{t("status.empty")}</InputLabel>
      <Select label={t("status.empty")} disabled labelId="batch" className="" defaultValue=""></Select>
    </FormControl>
  </div>

export const Failure = ({
  error,
}: CellFailureProps<FindBatchesSiteSelectQueryVariables>) => (
  <div style={{ color: 'red' }}>Error: {error?.message}</div>
)

export const Success = ({
  batches,
  idSite,
  select
}: CellSuccessProps<
  FindBatchesSiteSelectQuery,
  FindBatchesSiteSelectQueryVariables
> & BatchesSelectCellProps) => {
  const { idHarvest, idBatch } = useParams()
  const selectedBatch = batches.find(batch => (batch.id == parseInt(idBatch)) || (batch.harvest?.id == parseInt(idHarvest)))

  const isForecastAIPage = useMatch(routes.forecastWithAIBatch())

  const handleOnChange = (batch) => {
    //BatchPageSelect
    if (select && select.rootPath)
      navigate(`${select.rootPath}${batch.id}`)
    else if (select && batch[select.path]?.id)
      navigate(`?${select.urlSearchString}=${batch[select.path]?.id}`)
    else
      navigate("?idBatch=" + batch.id)
  }
  return (
    <div className="grid grid-cols-3 w-full h-14 items-center justify-evenly px-4 py-2 dark:bg-black/20 bg-slate-100/60 backdrop-blur-xl shadow-md relative z-20">
      <BatchReportTargetSelect className="w-44" variant="standard" />
      <div className="flex items-center justify-center">
        <BatchSelect batches={batches} className="w-44" onChange={handleOnChange} value={selectedBatch} showAIRequestsCount={isForecastAIPage.match} />
      </div>
      <div className="flex flex-col items-end align-middle">
        <Typography variant="h6" className="text-sm">
          {selectedBatch?.quantity} PL{selectedBatch?.batchInitial?.stadePL} {selectedBatch?.batchInitial?.provider.name}
        </Typography>
        <Typography variant="caption">
          {selectedBatch?.batchInitial?.growModel.name}
        </Typography>
      </div>
    </div>
  )
}
